<template>
  <div class="checkout-payment">
    <h3 class="checkout-payment__title">
      Способ оплаты и скидки
    </h3>

    <p class="checkout-payment__text">
      {{ text }}
    </p>

    <ui-notification
      :class="['payment-notification', { disabled: paymentIsNotAvailable }]"
      :title="notificationTitle"
    >
      <div :class="['notification-description', { hidden: adaptive.isMobile && paymentIsNotAvailable }]">
        {{ notificationDescription }}
      </div>

      <payment-icons
        class="notification-icons"
        :icon="paymentIcon"
      />

      <ui-button
        slot="action"
        :blue="adaptive.isMobile"
        :flat="!adaptive.isMobile"
        :class="['notification-button', { hidden: onlyOneMethodAvailable }]"
        text="Изменить"
        @click="openModal"
      />
    </ui-notification>

    <checkout-promo />
    <checkout-bonuses />

    <payment-methods-modal
      v-model="paymentMethodsOpened"
      :payment-methods="paymentMethods"
      :loading="!adaptive.isMobile && isLoading"
      @select="onSelectMethod"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { ACTIVE_PAYMENT_METHODS } from '@/constants/payment';
import { cartGtmEvents } from '@/gtm/cart-gtm-events';

/* Components */
import CheckoutPromo from '@/components/checkout/checkout-promo.vue';
import CheckoutBonuses from '@/components/checkout/checkout-bonuses.vue';
import PaymentIcons from '@/components/payment/payment-methods-modal/payment-icons.vue';
import PaymentMethodsModal from '@/components/payment/payment-methods-modal/payment-methods-modal.vue';

export default {
  name: 'checkout-payment',

  components: {
    PaymentMethodsModal,
    CheckoutPromo,
    CheckoutBonuses,
    PaymentIcons,
  },

  inject: ['adaptive'],

  data() {
    return {
      paymentMethodsOpened: false,
      isLoading: false,
    };
  },

  computed: {
    ...mapGetters({
      METHODS: 'cart/paymentMethods',
    }),

    paymentIsNotAvailable() {
      return !this.METHODS.length || !this.paymentMethods.find(({ selected }) => selected);
    },

    onlyOneMethodAvailable() {
      return this.paymentMethods.length <= 1
          && this.paymentMethods.find(({ selected }) => selected);
    },

    text() {
      return this.paymentIsNotAvailable
        ? 'Для выбора способа оплаты необходимо выбрать способ получения заказа'
        : 'Оплатите заказ онлайн на сайте или выберите другой способ оплаты';
    },

    notificationTitle() {
      return this.paymentIsNotAvailable
        ? 'Необходимо выбрать способ получения'
        : this.paymentMethod.altTitle || this.paymentMethod.title || '';
    },

    notificationDescription() {
      return this.paymentIsNotAvailable
        ? ''
        : this.paymentMethod?.subtitle;
    },

    paymentMethod() {
      return this.paymentMethods.find(({ selected }) => selected) || {};
    },

    paymentIcon() {
      return this.paymentIsNotAvailable
        ? 'IconCardMethodsDisabled'
        : this.paymentMethod?.altIcon
          ? this.paymentMethod.altIcon
          : this.paymentMethod.icon;
      // на один метод может приходиться 2 типа иконок
    },

    paymentMethods() {
      const availableMethods = this.METHODS
        .reduce((res, { method }) => (res[method] = true, res), {});

      const selectedMethod = this.METHODS.find(({ selected }) => selected);

      return ACTIVE_PAYMENT_METHODS
        .filter(({ value }) => availableMethods[value])
        .map(method => ({ ...method, selected: selectedMethod?.method === method.value }));
    },
  },

  watch: {
    paymentMethod: {
      immediate: true,
      handler(value, oldValue) {
        if (this.$isServer || this.isLoading) return;
        if (value?.value && value?.value !== oldValue?.value) {
          cartGtmEvents.paymentMethodPreselected({ method: value });
        }
      },
    }
  },

  methods: {
    ...mapActions({
      SET_METHOD: 'cart/setPaymentMethod',
    }),

    onSelectMethod({ value }) {
      this.isLoading = true;

      this.SET_METHOD(value)
        .then(() => {
          this.paymentMethodsOpened = false;
          cartGtmEvents.paymentMethodChanged({
            selectedMethod: value,
            availableMethods: this.paymentMethods,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    openModal() {
      this.paymentMethodsOpened = true;
      cartGtmEvents.changePaymentMethod();
    },
  },
};
</script>

<style lang="scss">
.checkout-payment {
  &__title {
    margin-bottom: 8px;
  }

  &__text {
    font-size: 14px;
    line-height: 20px;

    @include breakpoint("tablet") {
      font-size: 12px;
      line-height: 16px;
    }

    @include breakpoint("desktop") {
      font-size: 14px;
      line-height: 20px;
    }
  }

  .payment-notification {
    padding: 12px 12px 12px 16px;
    margin-top: 20px;

    &.disabled {
      border-color: $gray_l2;
      color: $gray_l2;

      .ui-notification-action {
        display: none;
      }
    }

    .ui-notification-action {
      margin-top: 8px;
      width: 100%;
    }

    .ui-notification-title {
      line-height: 20px;
    }

    .notification-subtitle {
      margin-top: 4px;
      font-size: 12px;
      line-height: 16px;
    }

    .notification-description {
      min-height: 16px;
      font-size: 12px;

      &.hidden {
        display: none;
      }
    }

    .notification-icons {
      margin-top: 8px;

      img {
        max-height: 16px;
      }
    }

    .notification-button {
      width: 100%;

      &.hidden {
        display: none;
      }
    }
  }

  &__online-pay {
    margin-top: 8px;
    line-height: 0;
  }

  .ui-radiocards__card {
    min-height: 100px;
  }
}

@include breakpoint("tablet") {
  .checkout-payment {
    &__online-pay {
      margin-top: 16px;
    }

    .payment-notification {
      display: flex;
      flex-direction: row;
      align-items: flex-start;

      .ui-notification-action {
        width: fit-content;
      }
    }
  }
}

@include breakpoint("desktop") {
  .checkout-payment {
    &__online-pay {
      margin-top: 8px;
    }

    .payment-notification {
      padding: 16px 20px;

      .ui-notification-title {
        line-height: 24px;
      }

      .notification-description {
        font-size: 14px;
      }

      .notification-icons img {
        max-height: 20px;
      }
    }
  }
}
</style>
